import { observable, action, computed, runInAction } from 'mobx';
import { get, put, AxiosResponse } from 'axios';
import { LabsStatus, UserLab } from 'types/labs';
import { DateTime } from 'luxon';
import UserStore from './User';
import { getLabDefinition } from '../services/LabService';
import { getCourseInfo } from '../services/ClassroomService';
import { USER_LAB_API, LABS_POLLING_MS } from '../config/constants';

export default class LabStore {
  @observable userStore: UserStore;

  @observable courseSlug = '';

  @observable labDefinition: any = {};

  @observable courseInfo: any = {};

  @observable labDefinitionFetched = false;

  @observable labBlueprintsFetched = false;

  @observable allUserLabs: UserLab[] = [];

  @observable allUserLabsFetched = false;

  pollingIntervalId: ReturnType<typeof setInterval> | null = null;

  currentPollingInterval: number | null = null;

  constructor(userStore: UserStore) {
    this.userStore = userStore;
  }

  @action getLabDefinition = async (newCourseSlug?: string): Promise<void> => {
    this.labDefinitionFetched = false;

    if (newCourseSlug) {
      this.courseSlug = newCourseSlug;
    }
    try {
      this.labDefinition = await getLabDefinition(this.courseSlug);
    } catch (err) {
      console.error(`couldn't fetch lab definition: ${err}`, err);
    } finally {
      this.labDefinitionFetched = true;
    }
  };

  @action getLabBlueprints = async (): Promise<void> => {
    this.labBlueprintsFetched = false;
    try {
      const courseInfo = await getCourseInfo(this.courseSlug);
      this.courseInfo = courseInfo || this.courseInfo;
    } catch (err) {
      console.error('failed to get course info:', err);
    } finally {
      this.labBlueprintsFetched = true;
    }
    return this.courseInfo;
  };

  @computed get labBlueprints(): any[] {
    const { lab_info: labInfo } = this.courseInfo || {};

    const { blueprint_id: blueprintId, lab_environment: labEnvironment } =
      labInfo || {};

    if (!(labEnvironment?.blueprints.length || blueprintId)) {
      return [];
    }

    return labEnvironment?.blueprints || [{ blueprint_id: blueprintId }];
  }

  @computed get courseCodeByLab(): Record<string, string> {
    return this.allUserLabs.reduce<Record<string, string>>((acc, userLab) => {
      if (userLab.doc_id && userLab.started_by_slug) {
        acc[userLab.doc_id] = userLab.started_by_slug;
      }
      return acc;
    }, {});
  }

  getCourseCode(userLabId: string): string | null {
    return this.courseCodeByLab[userLabId] ?? null;
  }

  async startPolling(): Promise<void> {
    // Dynamic polling:
    // Starts with LABS_POLLING_MS interval,
    // switches to ONE_MINUTE when a lab is close to shutting down,
    // and synchronizes polling across tabs for the same user.

    if (this.pollingIntervalId) return;
    this.fetchAllUserLabs();
    const delay = this.getUserPollingDelay();

    // Start aligned polling loop after calculated delay
    setTimeout(() => {
      if (!this.userStore?.isLoggedIn) return;
      this.fetchAllUserLabs();
      this.startPollingLoop();
    }, delay);
  }

  fetchAllUserLabs = async (): Promise<void> => {
    try {
      const result: AxiosResponse<{ items: UserLab[] }> = await get(
        USER_LAB_API,
      );

      runInAction(() => {
        this.allUserLabs = result.data.items?.length ? result.data.items : [];
        this.allUserLabsFetched = true;
      });
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('disabling polling due to session timeout');
        clearInterval(this.pollingIntervalId);
      }
      runInAction(() => {
        this.allUserLabsFetched = true;
      });
    }
  };

  getUserPollingDelay(): number {
    // Ensure all tabs for a single user poll at
    // the same time by computing a consistent delay
    const POLL_INTERVAL = this.computePollingInterval();

    const userIdentifier = this.userStore?.user?.username || 'guest';
    const hash = Array.from(userIdentifier).reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0,
    );

    // 7919 is just a large-ish prime number to spread out values more
    const userOffset = (hash * 7919) % POLL_INTERVAL;
    const now = Date.now();
    const baseAlignedTime = POLL_INTERVAL - (now % POLL_INTERVAL);

    return (baseAlignedTime - userOffset + POLL_INTERVAL) % POLL_INTERVAL;
  }

  computePollingInterval(): number {
    const ONE_MINUTE = 60 * 1000;

    const hasLabNearShutdown = this.allUserLabs.some((lab) => {
      const remainingMillisecs = this.remainingShutdownTime(lab.doc_id);
      return (
        lab.state === LabsStatus.Running &&
        remainingMillisecs < LABS_POLLING_MS &&
        remainingMillisecs >= ONE_MINUTE * 10 * -1 // Lab may shut down up to 10 minutes after its scheduled expiration time
      );
    });
    return hasLabNearShutdown ? ONE_MINUTE : LABS_POLLING_MS;
  }

  startPollingLoop(): void {
    const updatePollingInterval = () => {
      const newInterval = this.computePollingInterval();

      if (this.currentPollingInterval === newInterval) return;

      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
      }

      this.currentPollingInterval = newInterval;

      this.pollingIntervalId = setInterval(() => {
        this.pollingTick(updatePollingInterval);
      }, newInterval);
    };

    this.currentPollingInterval = this.computePollingInterval();

    this.pollingIntervalId = setInterval(() => {
      this.pollingTick(updatePollingInterval);
    }, this.currentPollingInterval);
  }

  async pollingTick(updatePollingInterval: () => void): Promise<void> {
    // Stop polling if the user is not logged in
    if (!this.userStore?.isLoggedIn) {
      this.stopPolling();
      return;
    }

    await this.fetchAllUserLabs();
    updatePollingInterval();
  }

  remainingShutdownTime(labId: string): number | null {
    const userLab = this.allUserLabs.find((lab) => lab.doc_id === labId);

    if (userLab?.scheduled_shutdown_time) {
      const shutdownTime = DateTime.fromISO(userLab.scheduled_shutdown_time);
      const now = DateTime.now();
      return Math.floor(shutdownTime.diff(now).as('milliseconds'));
    }

    return null;
  }

  stopPolling(): void {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
      this.currentPollingInterval = null;
    }
  }

  static dismissAlert = async (
    labId: string,
    command: string,
  ): Promise<any> => {
    if (!labId) {
      console.warn('No active lab ID provided');
      return {};
    }
    if (!command) {
      console.warn('No command provided');
      return {};
    }

    const url = `${USER_LAB_API}${labId}`;
    const result = await put(url, { command });

    return result;
  };

  static extendAutoStop = async (labId: string): Promise<any> => {
    const command = 'app_autostop_extend';

    if (!labId) {
      console.warn('No active lab ID provided');
      return {};
    }

    const url = `${USER_LAB_API}${labId}`;
    const result = await put(url, { command });

    return result;
  };

  isLabNearShutdown(lab: UserLab): boolean {
    const FIFTEEN_MINUTES = 15 * 60 * 1000;
    const remaining = this.remainingShutdownTime(lab.doc_id);

    return (
      lab.state === LabsStatus.Running &&
      remaining !== null &&
      remaining <= FIFTEEN_MINUTES &&
      remaining >= 0
    );
  }

  @computed get labsNearShutdown(): UserLab[] {
    return this.allUserLabs.filter((lab) => {
      const dismissed = lab.user_display_alerts?.near_shutdown_dismissed;
      return this.isLabNearShutdown(lab) && !dismissed;
    });
  }

  isShuttingDown = (labId: string): boolean => {
    // Checks if the lab was stopped in the
    // last 2 minutes or is currently stopping
    const userLab = this.allUserLabs.filter((u) => u.doc_id === labId)[0];
    const lastState = userLab.state_transition_log.pop();
    let justStopped = false;

    if (lastState) {
      const now = new Date();
      const whenDate = new Date(lastState.when);
      const delta = now.getTime() - whenDate.getTime();

      justStopped =
        lastState.prev_state === LabsStatus.Running &&
        lastState.desired_state === LabsStatus.Stopped &&
        delta < 2 * 60 * 1000; // last 2 minutes
    }

    const isStopping =
      userLab.state === LabsStatus.Running &&
      userLab.desired_state === LabsStatus.Stopped;

    return justStopped || isStopping;
  };

  @computed get labsRecentlyShutdown(): UserLab[] {
    // Return labs whose scheduled_shutdown_time has passed
    // AND that are now stopped or stopping
    // AND alert was not dismissed yet

    return this.allUserLabs.filter((lab) => {
      const shutdownTime = lab.scheduled_shutdown_time
        ? new Date(lab.scheduled_shutdown_time).getTime()
        : null;
      if (!shutdownTime || Date.now() < shutdownTime) return false;

      if (!this.isShuttingDown(lab.doc_id)) return false;

      const dismissed = lab.user_display_alerts?.recently_shutdown_dismissed;
      if (dismissed) return false;

      return true;
    });
  }
}
